import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompletedIcon,
  PlayArrow as InProgressIcon,
  PendingActions as PendingIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";

const Training = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");

  // Mock data
  const trainings = [
    {
      id: 1,
      schoolName: "Greenwood High School",
      trainer: "John Trainer",
      type: "Initial Setup",
      status: "Completed",
      startDate: "2024-05-15",
      endDate: "2024-05-20",
      duration: "5 days",
      participants: 12,
    },
    {
      id: 2,
      schoolName: "Sunshine Academy",
      trainer: "Jane Trainer",
      type: "Advanced Features",
      status: "In Progress",
      startDate: "2024-06-10",
      endDate: "2024-06-25",
      duration: "15 days",
      participants: 8,
    },
    {
      id: 3,
      schoolName: "Riverside School",
      trainer: "Mike Trainer",
      type: "System Update",
      status: "Scheduled",
      startDate: "2024-07-01",
      endDate: "2024-07-05",
      duration: "5 days",
      participants: 10,
    },
    {
      id: 4,
      schoolName: "Mountain View School",
      trainer: "Sarah Trainer",
      type: "Refresher Course",
      status: "Pending",
      startDate: null,
      endDate: null,
      duration: "3 days",
      participants: 0,
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "In Progress":
        return "info";
      case "Scheduled":
        return "warning";
      case "Pending":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CompletedIcon />;
      case "In Progress":
        return <InProgressIcon />;
      case "Scheduled":
        return <ScheduleIcon />;
      case "Pending":
        return <PendingIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const stats = {
    total: trainings.length,
    completed: trainings.filter((t) => t.status === "Completed").length,
    inProgress: trainings.filter((t) => t.status === "In Progress").length,
    scheduled: trainings.filter((t) => t.status === "Scheduled").length,
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Training Management
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Manage training sessions for school clients
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />}>
            Schedule Training
          </Button>
        </Box>

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Total Trainings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "success.main" }}>
                  {stats.completed}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "info.main" }}>
                  {stats.inProgress}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  In Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "warning.main" }}>
                  {stats.scheduled}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Scheduled
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Search trainings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>

        {/* Trainings Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>School</TableCell>
                    <TableCell>Trainer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Participants</TableCell>
                    <TableCell>Dates</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trainings.map((training) => (
                    <TableRow key={training.id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                            <SchoolIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {training.schoolName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{training.trainer}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{training.type}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(training.status)}
                          label={training.status}
                          color={getStatusColor(training.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{training.duration}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{training.participants}</Typography>
                      </TableCell>
                      <TableCell>
                        {training.startDate ? (
                          <Typography variant="body2">
                            {new Date(training.startDate).toLocaleDateString()} - {training.endDate ? new Date(training.endDate).toLocaleDateString() : "TBD"}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Not scheduled
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small">
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default Training;

